import { Injectable } from '@angular/core'

import { Hunt, GeneratedContent } from '@cyborg-monorepo/hunter/common'

export interface SelectedQueryPath {
  toolId: string
  huntQueryId: string
  pivotIds: string[]
}

@Injectable({ providedIn: 'root' })
export class QueryIndexService {
  /**
   * Cache of built indexes per hunt ID. Stores the Hunt reference alongside
   * the generated index so we can invalidate automatically when the Hunt
   * object changes.
   */
  private indexCache = new Map<
    string,
    { hunt: Hunt; index: Map<string, GeneratedContent> }
  >()

  /**
   * Build or retrieve from cache a flat map of all queries and pivots
   */
  build(hunt: Hunt): Map<string, GeneratedContent> {
    const huntId = hunt.id

    const cached = this.indexCache.get(huntId)
    if (cached?.hunt === hunt) {
      return cached.index
    }

    const map = new Map<string, GeneratedContent>()
    for (const pkg of hunt.huntPackages) {
      for (const env of pkg.analysis) {
        for (const tool of env.tools) {
          for (const q of tool.generatedContent?.Hunt ?? []) {
            if (q.id) {
              map.set(`${tool.id}:${q.id}`, q)
            }
            q.pivots?.forEach((p) =>
              this.indexPivotsRecursively(tool.id, p, map),
            )
          }
        }
      }
    }
    this.indexCache.set(huntId, { hunt, index: map })
    return map
  }

  /**
   * Select a query or pivot by path, using cached index
   */
  select(hunt: Hunt, path: SelectedQueryPath): GeneratedContent | undefined {
    const key = `${path.toolId}:${path.huntQueryId}`
    let current = this.build(hunt).get(key)
    if (!current) {
      return undefined
    }
    for (const id of path.pivotIds) {
      current = current.pivots?.find((p) => p.id === id) ?? undefined
      if (!current) {
        return undefined
      }
    }
    return current
  }

  /**
   * Clear cached index for a specific hunt or all hunts
   * @param huntId Optional hunt ID; if omitted, clears all cache
   */
  clearCache(huntId?: string): void {
    if (huntId) {
      this.indexCache.delete(huntId)
    } else {
      this.indexCache.clear()
    }
  }

  /**
   * Recursively index pivot records and their children
   */
  private indexPivotsRecursively(
    toolId: string,
    pivot: GeneratedContent,
    map: Map<string, GeneratedContent>,
  ): void {
    if (pivot.id) {
      map.set(`${toolId}:${pivot.id}`, pivot)
    }
    pivot.pivots?.forEach((child) =>
      this.indexPivotsRecursively(toolId, child, map),
    )
  }
  /**
   * Immutable update helper:
   * Walks every package/env/tool.generatedContent.Hunt tree,
   * applies `updater` to the one node whose id matches,
   * and returns a brand-new Hunt object.
   */
  updateGeneratedContent(
    hunt: Hunt,
    targetId: string,
    updater: (old: GeneratedContent) => GeneratedContent,
  ): Hunt {
    return {
      ...hunt,
      huntPackages: hunt.huntPackages.map((pkg) => ({
        ...pkg,
        analysis: pkg.analysis.map((env) => ({
          ...env,
          tools: env.tools.map((tool) => {
            const gc = tool.generatedContent
            if (!gc?.Hunt) return tool
            return {
              ...tool,
              generatedContent: {
                ...gc,
                Hunt: this._updateTree(gc.Hunt, targetId, updater),
              },
            }
          }),
        })),
      })),
    }
  }

  private _updateTree(
    nodes: GeneratedContent[],
    targetId: string,
    updater: (old: GeneratedContent) => GeneratedContent,
  ): GeneratedContent[] {
    return nodes.map((n) => {
      if (n.id === targetId) {
        return updater(n)
      }
      if (n.pivots?.length) {
        return {
          ...n,
          pivots: this._updateTree(n.pivots, targetId, updater),
        }
      }
      return n
    })
  }
}
